// API Services
import axios from 'axios';

let onUnauthorized = () => {
  window.location.href = '/login';
};

let onRefreshToken = null;

export const setAuthCallbacks = (unauthorizedCallback, refreshTokenCallback) => {
  onUnauthorized = unauthorizedCallback;
  onRefreshToken = refreshTokenCallback;
};

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

apiClient.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 防止短时间内多次 401 重复弹出“已登出”
let isHandlingUnauthorized = false;

function handleUnauthorized() {
  if (isHandlingUnauthorized) {
    return;
  }
  isHandlingUnauthorized = true;
  onUnauthorized();
  // 1.5s 后允许再次弹窗（避免并发 401 刷屏，但不阻塞后续真实登出）
  setTimeout(() => {
    isHandlingUnauthorized = false;
  }, 1500);
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 非 401 或未携带请求配置，直接拒绝
    if (error.response?.status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    // 已经是刷新请求且失败：交给刷新逻辑统一处理，这里不再单独弹窗（避免重复“已登出”）
    if (originalRequest.url?.includes('/admin/refresh')) {
      return Promise.reject(error);
    }

    // 防止递归：同一请求只尝试刷新一次
    if (originalRequest._retry) {
      // 刷新仍失败：非静默请求才弹“已登出”
      if (!originalRequest._silent) {
        handleUnauthorized();
      }
      return Promise.reject(error);
    }

    if (onRefreshToken) {
      originalRequest._retry = true;
      const newToken = await onRefreshToken();
      if (newToken) {
        return apiClient(originalRequest);
      }
    }
    // 刷新失败且无新令牌：非静默请求才弹“已登出”（静默探测如初始化校验则静默拒绝）
    if (!originalRequest._silent) {
      handleUnauthorized();
    }
    return Promise.reject(error);
  }
);

export default apiClient;

export const dashboardApi = {
  overview: (days = 7) => apiClient.get('/api/v1/dashboard/overview', { params: { days } })
};

export const fileApi = {
  list: (page, perPage, extraParams = {}) => apiClient.get('/api/v1/files', { params: { page, per_page: perPage, ...extraParams } }),
  report: (data) => apiClient.post('/api/v1/file/report', data),
  delete: (id) => apiClient.delete(`/api/v1/files/${id}`),
  download: (id) => apiClient.get(`/api/v1/files/${id}/download`, {
    responseType: 'blob',
    timeout: 300000  // 5分钟
  }),
  downloadBySession: (sessionId) => apiClient.post('/api/v1/files/download-by-session', { session_id: sessionId }, {
    responseType: 'blob',
    timeout: 300000  // 5分钟
  }),
  getStats: () => apiClient.get('/api/v1/files/stats'),
  update: (id, data) => apiClient.put(`/api/v1/files/${id}`, data),
  updateNote: (id, remark) => apiClient.put(`/api/v1/files/${id}`, { remark }),
  get: (id) => apiClient.get(`/api/v1/files/${id}`)
};

export const userApi = {
  list: (params) => apiClient.get('/api/v1/admin/users', { params }),
  create: (data) => apiClient.post('/api/v1/admin/users', data),
  update: (id, data) => apiClient.put(`/api/v1/admin/users/${id}`, data),
  delete: (id) => apiClient.delete(`/api/v1/admin/users/${id}`),
  resetPassword: (id, password) => apiClient.post(`/api/v1/admin/users/${id}/reset-password`, { password }),
  // 初始化校验：未登录是预期情况，标记 _silent 让拦截器不弹“已登出”
  getCurrent: () => apiClient.get('/api/v1/admin/me', { _silent: true }),
  updateCurrent: (data) => apiClient.put('/api/v1/admin/me', data)
};

export const auditApi = {
  list: (params) => apiClient.get('/api/v1/admin/audit-logs', { params })
};

export const banApi = {
  list: (params) => apiClient.get('/api/v1/admin/bans', { params }),
  unban: (id) => apiClient.post(`/api/v1/admin/bans/${id}/unban`)
};

export const subscriptionApi = {
  listMine: () => apiClient.get('/api/v1/subscriptions'),
  createMine: (data) => apiClient.post('/api/v1/subscriptions', data),
  updateMine: (id, data) => apiClient.patch(`/api/v1/subscriptions/${id}`, data),
  deleteMine: (id) => apiClient.delete(`/api/v1/subscriptions/${id}`),
  listAll: (params) => apiClient.get('/api/v1/admin/subscriptions', { params }),
  listNotifications: (params) => apiClient.get('/api/v1/admin/subscriptions/notifications', { params }),
  createOnBehalf: (data) => apiClient.post('/api/v1/admin/subscriptions', data),
  updateAny: (id, data) => apiClient.patch(`/api/v1/admin/subscriptions/${id}`, data),
  deleteAny: (id) => apiClient.delete(`/api/v1/admin/subscriptions/${id}`),
  sendTestEmail: (email) => apiClient.post('/api/v1/admin/subscriptions/test-email', { email }),
  resendNotification: (id) => apiClient.post(`/api/v1/admin/subscriptions/notifications/${id}/resend`)
};

export const systemApi = {
  getSettings: () => apiClient.get('/api/v1/admin/system-settings'),
  updateSettings: (data) => apiClient.put('/api/v1/admin/system-settings', data)
};

export const authApi = {
  login: (username, password) => apiClient.post('/api/v1/admin/login', { username, password }),
  refreshToken: () => apiClient.post('/api/v1/admin/refresh', {}),
  changePassword: (data) => apiClient.post('/api/v1/admin/change-password', data)
};

// 用户 API Key（本人自助 + 管理员代建/管理）
export const apiKeyApi = {
  // 本人
  listMine: () => apiClient.get('/api/v1/user/api-keys'),
  createMine: (data) => apiClient.post('/api/v1/user/api-keys', data),
  revokeMine: (id) => apiClient.delete(`/api/v1/user/api-keys/${id}`),
  rotateMine: (id) => apiClient.post(`/api/v1/user/api-keys/${id}/rotate`),
  // 管理员
  listAll: (params) => apiClient.get('/api/v1/admin/api-keys', { params }),
  createForUser: (data) => apiClient.post('/api/v1/admin/api-keys', data),
  revokeAny: (id) => apiClient.delete(`/api/v1/admin/api-keys/${id}`),
  rotateAny: (id) => apiClient.post(`/api/v1/admin/api-keys/${id}/rotate`)
};

export const sftpApi = {
  getChallenge: (data) => apiClient.post('/api/v1/challenge', data),
  verifyChallenge: (data) => apiClient.post('/api/v1/challenge/verify', data),
  getCredential: (data) => apiClient.post('/api/v1/sftp/credential', data),
  list: (page, perPage, extraParams = {}) => apiClient.get('/api/v1/admin/sftp-users', { params: { page, per_page: perPage, ...extraParams } }),
  get: (id) => apiClient.get(`/api/v1/admin/sftp-users/${id}`),
  create: (data) => apiClient.post('/api/v1/admin/sftp-users', data),
  delete: (id) => apiClient.delete(`/api/v1/admin/sftp-users/${id}`),
  resetPassword: (id, password) => apiClient.post(`/api/v1/admin/sftp-users/${id}/reset-password`, { password }),
  updateExpires: (id, expires_in_hours) => apiClient.put(`/api/v1/admin/sftp-users/${id}/expires`, { expires_in_hours })
};

const publicApiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

export const shareApi = {
  create: (data) => apiClient.post('/api/v1/share/create', data),
  list: (params) => apiClient.get('/api/v1/share/list', { params }),
  get: (id) => apiClient.get(`/api/v1/share/${id}`),
  delete: (id) => apiClient.delete(`/api/v1/share/${id}`),
  disable: (id) => apiClient.put(`/api/v1/share/${id}/disable`),
  // 公开接口（无需登录，不携带 cookie）
  getPublic: (shareCode) => publicApiClient.get(`/api/v1/share/${shareCode}`),
  verifyPassword: (shareCode, password) => publicApiClient.post(`/api/v1/share/${shareCode}/verify`, { password }),
  download: (shareCode, password) => {
    const body = password ? { password } : {};
    return publicApiClient.post(`/api/v1/share/${shareCode}/download`, body, {
      responseType: 'blob',
      timeout: 300000
    });
  }
};

// 会话附件管理端接口（沿用 JWT Cookie 鉴权）
export const attachmentApi = {
  // 按 SessionID 分页查询附件,支持 content_type 筛选
  listBySession: (sessionId, params) =>
    apiClient.get(`/api/v1/admin/sessions/${sessionId}/attachments`, { params }),
  // 附件详情(含 text_content)
  get: (id) => apiClient.get(`/api/v1/admin/attachments/${id}`),
  // 下载附件(文件型返回二进制流,text 型返回 JSON)
  download: (id) =>
    apiClient.get(`/api/v1/admin/attachments/${id}/download`, {
      responseType: 'blob',
      timeout: 300000
    }),
  // 软删除附件
  delete: (id) => apiClient.delete(`/api/v1/admin/attachments/${id}`),
  // 打包下载整会话附件 ZIP 的 URL(浏览器直接跳转,自动携带 Cookie)
  downloadAllUrl: (sessionId) =>
    `/api/v1/admin/sessions/${sessionId}/attachments/download-all`,
  // 会话列表(分页,合并附件与文件统计)
  listSessions: (params) =>
    apiClient.get('/api/v1/admin/sessions', { params }),
  // 会话内 PC 端上传文件列表(分页)
  listSessionFiles: (sessionId, params) =>
    apiClient.get(`/api/v1/admin/sessions/${sessionId}/files`, { params }),
  // 会话客户端信息(来自 ChallengeRecord,即使无 files 也有)
  getClientInfo: (sessionId) =>
    apiClient.get(`/api/v1/admin/sessions/${sessionId}/client-info`),
  // 打包下载会话中所有 PC 端文件(返回blob)
  downloadAllFiles: (sessionId) =>
    apiClient.post('/api/v1/files/download-by-session', { session_id: sessionId }, {
      responseType: 'blob',
      timeout: 300000
    }),
  // 打包下载整会话:会话详情 description_{sessionID}.txt + 全部文件 + 全部附件
  downloadPackage: (sessionId) =>
    apiClient.get(`/api/v1/admin/sessions/${sessionId}/download-package`, {
      responseType: 'blob',
      timeout: 300000
    }),
  // ── 会话基础信息编辑（管理端） ──
  getProfile: (sessionId) =>
    apiClient.get(`/api/v1/admin/sessions/${sessionId}/profile`),
  updateProfile: (sessionId, data) =>
    apiClient.put(`/api/v1/admin/sessions/${sessionId}/profile`, data),
  // ── 手工上传（管理端） ──
  // 附件（audio/video/image）：formData 含 file/content_type/remark
  // 必须显式指定 multipart：apiClient 默认 Content-Type=application/json，
  // axios transformRequest 会据此把 FormData 序列化为 JSON，导致后端 request.files 为空（file is required）
  uploadAttachment: (sessionId, formData) =>
    apiClient.post(`/api/v1/admin/sessions/${sessionId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  // 日志文件（进入会话文件体系）：formData 含 file/remark
  uploadManualFile: (sessionId, formData) =>
    apiClient.post(`/api/v1/admin/sessions/${sessionId}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
};